import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ORDER } from "@/constants/testIds";

const msgKey = (m, idx) =>
  m?.resend_received_id || `${m?.at || "no-ts"}-${m?.from_id || m?.from_email || idx}`;

/** Threaded messages between member and pro shop. */
export default function MessagesThread({ order, currentUserRole, onPosted }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    if (!message.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/orders/${order.order_id}/messages`, { text: message });
      onPosted?.(data);
      setMessage("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = currentUserRole === "club_admin";
  const messages = order.messages || [];

  return (
    <section className="surface-card mt-8 p-8">
      <div className="overline">Correspondence</div>
      <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Messages</h2>
      <div className="mt-5 space-y-3">
        {messages.length === 0 && <p className="text-sm text-[#7A7A7A]">No messages yet.</p>}
        {messages.map((m, i) => {
          const mine = m.from_role === currentUserRole;
          return (
            <div key={msgKey(m, i)} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-md border px-4 py-3 ${
                  mine
                    ? "bg-[#1A422D] text-white border-[#1A422D]"
                    : "bg-[#F3F1E9] text-[#1A1A1A] border-[#E5E3D8]"
                }`}
              >
                <div className={`text-xs uppercase tracking-wider mb-1 ${mine ? "text-[#D9C68A]" : "text-[#8a6f33]"}`}>
                  {m.from_role === "club_admin" ? "Pro Shop" : "Member"} · {m.from_name}
                  {m.is_email && (
                    <span
                      className={`ml-2 px-1.5 py-0.5 text-[9px] tracking-[0.18em] border ${
                        mine ? "border-[#D9C68A] text-[#D9C68A]" : "border-[#B59A5A] text-[#8a6f33]"
                      }`}
                    >
                      {m.inbound ? "EMAIL · REPLY" : "EMAIL"}
                    </span>
                  )}
                </div>
                <div className="text-sm leading-snug whitespace-pre-wrap">{m.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-[#D9C68A]" : "text-[#7A7A7A]"}`}>
                  {new Date(m.at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 flex gap-3 items-end">
        <textarea
          rows={2}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid={ORDER.detailMessage}
          className="input-field flex-1"
          placeholder={isAdmin ? "Reply to the member…" : "Send a message to the pro shop…"}
        />
        <button
          type="button"
          onClick={send}
          disabled={busy || !message.trim()}
          data-testid={ORDER.detailMessageSend}
          className="btn-primary"
        >
          Send
        </button>
      </div>
      {error && (
        <div className="mt-3 px-3 py-2 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error}
        </div>
      )}
    </section>
  );
}
